import type { AxiosResponse } from "axios";
import catalog from "@granular/fabric-catalog";
import { v4 as uuidv4 } from "uuid";
import { getAuthToken } from "./granularAxios";

import { axiosInstance, computeHeaders } from "./axios";
import {
  BasicFarm,
  FarmResponse,
  FarmsResponse,
  FarmsNetworkResponse,
} from "../models/farms";

/**
 * Get a list of farms for the given operation
 *
 * TODO: Recursive calls to fetch *all* entities are a temporary measure
 * to not break existing things.
 *
 * Couple of considerations here:
 *
 * 1) The *component* that paginates should make requests (via hooks or
 *    Redux actions) *on demand* for the next 'page' of results. If you
 *    have 9 'pages' of operations and the user only needs 4, you've
 *    made 5 unnecessary calls.
 *
 * 2) *Upstream APIs must be held responsible* for proper (a) sorting
 *    (b) searching and (c) pagination. It is the most ludicrous thing
 *    to burn CPU cycles, memory, and bandwidth doing all these things
 *    right in the client's ****ing browser. With all the talk around
 *    'optimizing' the frontend experience, this is the lowest-hanging
 *    fruit #FIXUPSTREAMFIRST
 *
 */
export const getFarms = async (
  {
    authToken,
    userId,
    operationId,
    pageSize = 1000,
    offset = 0,
    loadedItems = [],
  }: {
    authToken: string;
    userId: string;
    operationId: string;
    pageSize?: number;
    offset?: number;
    loadedItems?: FarmResponse[];
  },
  catalogFlag?: boolean,
): Promise<FarmsResponse> => {
  let response;
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    response = await catalog.fields.getFarms(
      { limit: pageSize, offset },
      catalogConfig,
    );
  } else {
    response = await axiosInstance.request<
      never,
      AxiosResponse<FarmsNetworkResponse>
    >({
      method: "GET",
      url: `/api/fields-svc/v2/farm?limit=${pageSize}&offset=${offset}`,
      headers: computeHeaders({
        authToken,
        userId,
        operationId,
      }),
    });
  }

  if (response.data.farms?.length) {
    loadedItems = loadedItems.concat(response.data.farms);

    if (response.data._metadata.count > Math.max(loadedItems.length, offset)) {
      return getFarms(
        {
          authToken,
          userId,
          operationId,
          pageSize,
          offset: response.data._metadata.offset + pageSize,
          loadedItems,
        },
        catalogFlag,
      );
    }
  }

  return { farms: loadedItems, count: loadedItems.length };
};

/*
 * Adds a farm to the given operation.
 */
const addFarm = async (
  {
    authToken,
    userId,
    operationId,
    farmName,
  }: {
    authToken: string;
    userId: string;
    operationId: string;
    farmName: string;
  },
  catalogFlag?: boolean,
): Promise<BasicFarm> => {
  let response;
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    response = await catalog.fields.addFarm(
      { id: uuidv4(), name: farmName },
      catalogConfig,
    );
  } else {
    response = await axiosInstance.request({
      method: "POST",
      url: "/api/fields-svc/v2/farm",
      headers: computeHeaders({
        authToken,
        userId,
        operationId,
      }),
      data: { id: uuidv4(), name: farmName },
    });
  }

  return {
    id: response.data.id,
    name: response.data.name,
    fieldCount: response.data.field_count,
  };
};

const removeFarm = async (
  {
    authToken,
    userId,
    operationId,
    farmId,
  }: {
    authToken: string;
    userId: string;
    operationId: string;
    farmId: string;
  },
  catalogFlag?: boolean,
): Promise<null> => {
  let response;
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    response = await catalog.fields.deleteFarm(farmId, catalogConfig);
  } else {
    response = await axiosInstance.request({
      method: "DELETE",
      url: `/api/fields-svc/v2/farm/${farmId}`,
      headers: computeHeaders({
        authToken,
        userId,
        operationId,
      }),
    });
  }
  return response.data;
};

const updateFarm = async (
  {
    authToken,
    userId,
    operationId,
    farm,
  }: {
    authToken: string;
    userId: string;
    operationId: string;
    farm: BasicFarm;
  },
  catalogFlag?: boolean,
): Promise<BasicFarm> => {
  let response;
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    response = await catalog.fields.updateFarm(farm, catalogConfig);
  } else {
    response = await axiosInstance.request({
      method: "PUT",
      url: `/api/fields-svc/v2/farm/${farm.id}`,
      headers: computeHeaders({
        authToken,
        userId,
        operationId,
      }),
      data: farm,
    });
  }
  return response.data;
};

export default {
  getFarms,
  addFarm,
  removeFarm,
  updateFarm,
};
